import {Provider} from 'mobx-react';
import {WithRouterProps} from 'next/dist/client/with-router';
import {withRouter} from 'next/router';
import React, {useContext} from 'react';

import {AsyncPriceCourseCardContext} from '@udemy/browse-course';
import {GwgAccelerator} from '@udemy/career-academies';
import {DiscoveryUnit} from '@udemy/discovery-api';
import {withI18n, WithI18nProps} from '@udemy/i18n';
import {MarketplaceOnly} from '@udemy/react-brand-components';
import {
    CertificationBundle,
    CertificationModel,
    CertificationUnit,
} from '@udemy/react-certification-preparation';
import {useExamByTopicId} from '@udemy/react-certification-vouchers';
import {CourseGuidanceTakeShortAssessmentEntrypoint} from '@udemy/react-course-highlights';
import {ValueProps} from '@udemy/react-discovery-units';
import {ExperimentationContext} from '@udemy/react-experimentation-provider';
import {EntryPointPopUp} from '@udemy/react-globalization-components/course-guidance';
import {ContentArea} from '@udemy/react-layout-components';
import {CONTEXT_TYPES} from '@udemy/resource-context-menu';
import {
    SubscriptionPlanContext,
    SubscriptionPlanSubscriberState,
    ConsumerSubscriptionGwGStandalonePlan,
    ConsumerSubscriptionGwGPersonalPlan,
} from '@udemy/subscription-graphql';
import {withUDData, WithUDDataProps} from '@udemy/ud-data';

import {resolveTopicThreeCardVerticalLayoutExperiment} from 'src/lib/experimentation-helper';
import ResourceContextMenuProvider from 'udemy-django-static/js/organization-common/resource-context-menu/resource-context-menu-provider.react-component';

import {LearnMoreSection} from './components/learn-more-section.react-component';
import {
    TopicDiscoveryListContainerWrapper,
    TopicDiscoveryUnitsContainerWrapper,
    TopicDiscoveryUnitsContainerWrapperLoader,
} from './components/topic-discovery-units-wrapper.react-component';
import {TopicEnrollmentStats} from './components/topic-enrollment-stats.react-component';
import {TopicHeader} from './components/topic-header.react-component';
import {TopicStaticUnits} from './components/topic-static-units.react-component';
import {useTopicPageData} from './hooks/use-topic-page-data';
import styles from './topic.module.less';
import {TopicPageExperimentVariants} from './topic.react-component';
import {TopicContext} from './types/topic';

// Stable reference to avoid MobX Provider "stores have changed" error on re-renders
const RESOURCE_CONTEXT_MENU_PROPS = {context: CONTEXT_TYPES.ALL_COURSES};

export interface TopicProps
    extends TopicContext,
        TopicPageExperimentVariants,
        WithUDDataProps,
        WithI18nProps,
        WithRouterProps {
    subsCollectionIds?: string;
    isConsumerSubsSubscriber: boolean;
    isSubsLoading?: boolean;
    certification?: CertificationModel;
    certificationBundles: CertificationBundle[];
    /** Slug-based cert detection — see `TopicProps.isCertificationTopic` for rationale. */
    isCertificationTopic?: boolean;
    enableLabsInPersonalPlan?: boolean;
    enableAssessmentsInPersonalPlan?: boolean;
    isLowSupplyTopicPage?: boolean;
    activeSubscriptionPlan?: SubscriptionPlanSubscriberState;
    gwgSubscriptionPlan?: SubscriptionPlanContext<ConsumerSubscriptionGwGStandalonePlan>;
    gwgPlusPPSubscriptionPlan?: SubscriptionPlanContext<ConsumerSubscriptionGwGPersonalPlan>;
    showGwgCarousel?: boolean;
    gwgCarouselAccelerator?: GwgAccelerator;
}

const InternalTopic: React.FC<TopicProps> = (props) => {
    const {
        activeSubscriptionPlan,
        avgRating,
        browseAggregatorPageEnabled,
        certification,
        certificationBundles,
        certDescription,
        certVouchersShowAddToCart,
        gwgCarouselAccelerator,
        isConsumerSubsSubscriber,
        isTopicVoucherModuleAboveBundle,
        numCourses,
        numExercises,
        presetValue,
        primaryDescription,
        questionsAndAnswers,
        relatedCertificationBrowseAggregators,
        relatedPages,
        showCertificationTopicHeaderFeature,
        showCodingExercisesBadge,
        showCourseGuidance,
        showGwgCarousel,
        showPersonalPlanBadge,
        tertiaryDescription,
        topic,
        topicTitle,
    } = props;

    const {
        bundleVariant,
        discoveryListRef,
        hasReplacementTitle,
        isMobile,
        isTopicDataLoading,
        isUserDataLoading,
        localizedEnrollmentStat,
        certHeroNumLearners,
        pageType,
        presetFilters,
        recommendationUnits,
        relatedCategories,
        showCertificationUnit,
        showLearnMoreSection,
        discoveryUnitFilter,
        handlePageChange,
        onLoadMore,
    } = useTopicPageData(props);

    const {voucherPool, examId, isAvailableForRegion} = useExamByTopicId(topic.id);
    const hasVoucher =
        !!voucherPool && !!examId && isAvailableForRegion && voucherPool.hasAvailableVouchers;

    const hasPracticeExam = (certificationBundles ?? []).some((bundle) =>
        (bundle.contents ?? []).some(
            (course) =>
                'is_practice_test_course' in course && course.is_practice_test_course === true,
        ),
    );

    const {featureVariantAssignments} = useContext(ExperimentationContext);
    const threeCardVerticalLayoutExperimentIsEnabled =
        resolveTopicThreeCardVerticalLayoutExperiment(
            props.threeCardVerticalLayoutExperimentIsEnabled,
            featureVariantAssignments,
        );
    const showMobileVerticalProductBrowseCards =
        !!isMobile && threeCardVerticalLayoutExperimentIsEnabled;
    const wrapTopicDiscoveryInContentArea = showMobileVerticalProductBrowseCards;

    const verticalRecoCourseCardContextValue = showMobileVerticalProductBrowseCards
        ? {
              useCourseProductCard: true,
              hideCourseDescription: true,
          }
        : {};

    const relatedPagesHeadline = {
        title: interpolate(gettext('Free %(topicName)s lessons'), {topicName: topic.name}, true),
        secondaryText: gettext('Bite-sized learning in minutes'),
        className: styles['section-container'],
        layoutVariant: isMobile ? 'compact' : 'default',
    };

    const topicDiscoverySection = (
        <TopicDiscoveryUnitsContainerWrapper
            {...props}
            isMobile={isMobile}
            pageType={pageType}
            discoveryUnitFilter={discoveryUnitFilter}
            ppMerchandisingVariant={props.isCertificationTopic ? 'cert' : 'general'}
            units={recommendationUnits as DiscoveryUnit[]}
            disableInfiniteScroll
            onLoadMore={onLoadMore}
        >
            {!hasReplacementTitle && localizedEnrollmentStat && (
                <TopicEnrollmentStats
                    data-item-index={0}
                    enrollmentStat={localizedEnrollmentStat}
                />
            )}
            {showCertificationUnit && (
                <CertificationUnit
                    data-item-index={0}
                    className="component-margin"
                    topicId={topic.id}
                />
            )}
            <div className="component-margin" data-item-index={-2} ref={discoveryListRef}>
                <TopicDiscoveryListContainerWrapper
                    {...props}
                    isMobile={isMobile}
                    pageType={pageType}
                    presetFilters={presetFilters}
                    handlePageChange={handlePageChange}
                />
            </div>
            <ValueProps className="component-margin" />
        </TopicDiscoveryUnitsContainerWrapper>
    );

    const pageBody = (
        <>
            <TopicHeader
                avgRating={avgRating}
                browseAggregatorPageEnabled={browseAggregatorPageEnabled}
                certification={certification}
                certDescription={certDescription}
                hasVoucher={hasVoucher}
                hasPracticeExam={hasPracticeExam}
                isLoading={isUserDataLoading}
                isExperimentConfigsLoading={props.isExperimentConfigsLoading}
                isMobile={!!isMobile}
                localizedEnrollmentStat={localizedEnrollmentStat}
                certHeroNumLearners={certHeroNumLearners}
                numCourses={numCourses}
                numExercises={numExercises}
                pageType={pageType}
                presetValue={presetValue}
                relatedCategories={relatedCategories}
                relatedCertificationBrowseAggregators={relatedCertificationBrowseAggregators}
                showCertificationTopicHeaderFeature={showCertificationTopicHeaderFeature}
                tertiaryDescription={tertiaryDescription}
                topicName={topic.name}
                topicTitle={topicTitle}
                topicImages={topic.images}
            />
            <div className="ud-container ud-page-wrapper">
                <TopicStaticUnits
                    showGwgCarousel={showGwgCarousel}
                    gwgCarouselAccelerator={gwgCarouselAccelerator}
                    activeSubscriptionPlan={activeSubscriptionPlan}
                    bundleVariant={bundleVariant}
                    topicId={topic.id}
                    isTopicVoucherModuleAboveBundle={isTopicVoucherModuleAboveBundle}
                    certVouchersShowAddToCart={certVouchersShowAddToCart}
                    isMobile={isMobile}
                    topicCatSubcatMwebCardStandardizationFeature={
                        props.topicCatSubcatMwebCardStandardizationFeature
                    }
                    useVerticalRecoCourseCardStyle={showMobileVerticalProductBrowseCards}
                    isExperimentConfigsLoading={props.isExperimentConfigsLoading}
                />
                {showCourseGuidance && (
                    <div className="component-margin" data-item-index={3}>
                        <CourseGuidanceTakeShortAssessmentEntrypoint />
                    </div>
                )}
                <EntryPointPopUp />
                {!isTopicDataLoading ? (
                    <Provider
                        isConsumerSubsSubscriber={isConsumerSubsSubscriber}
                        showPersonalPlanBadge={showPersonalPlanBadge}
                        showCodingExercisesBadge={showCodingExercisesBadge}
                    >
                        <ResourceContextMenuProvider
                            resourceContextMenuProps={RESOURCE_CONTEXT_MENU_PROPS}
                        >
                            {wrapTopicDiscoveryInContentArea ? (
                                <ContentArea>{topicDiscoverySection}</ContentArea>
                            ) : (
                                topicDiscoverySection
                            )}
                        </ResourceContextMenuProvider>
                    </Provider>
                ) : (
                    <TopicDiscoveryUnitsContainerWrapperLoader isMobile={!!isMobile} />
                )}
                {showLearnMoreSection && (
                    <MarketplaceOnly>
                        <div className={styles['learn-more-section-container']}>
                            <LearnMoreSection
                                className={styles['learn-more-section']}
                                topic={topic}
                                primaryDescription={primaryDescription}
                                questionsAndAnswers={questionsAndAnswers}
                                relatedPagesHeadline={{...relatedPagesHeadline}}
                                relatedPages={relatedPages}
                                presetValue={presetValue}
                                isMobile={!!isMobile}
                            />
                        </div>
                    </MarketplaceOnly>
                )}
            </div>
        </>
    );

    return showMobileVerticalProductBrowseCards ? (
        <AsyncPriceCourseCardContext.Provider value={verticalRecoCourseCardContextValue}>
            {pageBody}
        </AsyncPriceCourseCardContext.Provider>
    ) : (
        pageBody
    );
};

export const Topic = withI18n(withUDData(withRouter(InternalTopic)));
